import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, Calculator } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/data/content";
import { LogoWordmark } from "@/components/Logo";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-slate-100" : "bg-white/60 backdrop-blur-md"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo" aria-label={COMPANY.name}>
          <LogoWordmark markSize={36} withSlogan />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[#2873B8]" : "text-[#0C2749] hover:text-[#2873B8]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            className="flex items-center gap-2 text-sm font-medium text-[#0C2749] hover:text-[#2873B8] transition-colors"
            data-testid="navbar-phone"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone}
          </a>
          <Link
            to="/preisrechner"
            className="btn-shimmer inline-flex items-center gap-2 bg-[#0C2749] hover:bg-[#2873B8] text-white px-5 py-3 text-sm font-medium rounded-sm transition-colors"
            data-testid="navbar-calculator-cta"
          >
            <Calculator className="w-4 h-4" />
            Preis berechnen
          </Link>
        </div>

        <button
          aria-label="Menü öffnen"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-sm border border-slate-200 text-[#0C2749]"
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="navbar-mobile-menu">
          <div className="max-w-[1400px] mx-auto px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base font-medium ${isActive ? "text-[#2873B8]" : "text-[#0C2749]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="hairline my-2" />
            <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-2 text-[#0C2749]">
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
            <Link
              to="/preisrechner"
              className="bg-[#0C2749] text-white text-center px-5 py-3 rounded-sm font-medium"
              data-testid="navbar-mobile-cta"
            >
              Jetzt Preis berechnen
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
